/**
 * contracts.md §5 — **the one place a route is named** (D148).
 *
 * Until this file existed, a route was named as an independent string literal at every layer
 * that mentioned it: the server registry, the client's three path-building sites, the dev fake,
 * and — as a subtree prefix — the edge. Nothing compared them except
 * `infra/tests/route-coverage.test.ts`, a regex sweep over source text that needed a
 * `KNOWN_GAPS` escape hatch to *express* a mismatch it could not fix.
 *
 * **That was the root cause of the latch `404`, not an accident of it**: P7's mode buttons
 * called a plausible, guessed `PATCH /gates/{gateId}/latch` while P8 shipped
 * `POST /gates/{gateId}/state`, and the gap survived as a permitted exemption — nothing paged,
 * no test reddened, and the fake's deliberate `404` mirror meant even a local run reproduced the
 * failure without anyone reading it as one. The guard's own history bounds what a regex can do:
 * it passed **vacuously twice**, for three separate reasons — an extractor that stopped at the
 * comma inside `path(gateId, '/latch')`, silently skipping the very route it was written for; a
 * backtick counted as a nesting opener when it toggles; and a regex naming `registerAuthedRoute`
 * that could not see P5's `registerMemberRoute` wrapper, hiding all seven membership routes.
 *
 * So the comparison moved into the type system. **`ROUTES` is the only producer of a
 * `RoutePath`**, and `registerAuthedRoute`/`registerPublicRoute` accept nothing else, so a
 * registration written as a bare string literal is a compile error rather than a test failure.
 * The client builds every path from the same entry, which makes "a route the client names that
 * the server does not register" a **missing property on `ROUTES`** — again a compile error.
 *
 * **Four rules this file must keep** (D148, R3-4/R3-5):
 *
 *  1. **Browser-bundleable.** No `node:` specifier at module scope — a table of string constants
 *     and one pure function satisfies that trivially, which is *why* the table lives in
 *     `@selogate/core` rather than in a new package `web` would have to depend on.
 *  2. **{@link buildPath} percent-encodes and does nothing else.** It is not a normalizer:
 *     identifier repair happens once, at the edge (D123), CI check 10 fails the build on a second
 *     eight-digit validator/formatter/normalizer outside `gate-id.ts`, and a builder that also
 *     normalized would re-open D131's silence failure mode — an upper-cased lower-case
 *     `BrokerCode` provisions cleanly, connects, publishes, and is never heard. **Encoding is not
 *     repair.**
 *  3. **An entry carries no handler, no schema, and no auth flag.** The table names routes. One
 *     that grew a `requiresManager` field would become a second place authorization is decided,
 *     which §13 "One evaluator (D101)" — CI check 8 — exists to forbid. **`rateLimit` (D157) is
 *     not that field, and the line between them is worth stating**: authorization asks *may this
 *     caller do this*, is per-principal, per-gate and per-role, and has exactly one evaluator; a
 *     rate-limit posture asks *how often may anyone do this*, is a property of the route rather
 *     than of the caller, and was **already** decided outside the handler — by
 *     `isGateScopedPath()`, from the path's own text. D157 moved that decision from an inference
 *     to a declaration; it did not move it into the table from somewhere it was safer. The test
 *     that keeps the line honest is the one that forbids the *reverse*: no entry may carry a
 *     field a handler would have to consult.
 *  4. **An entry is a claim that a route exists.** `{proxy+}` reservations get none — and P14's
 *     `/gates/{gateId}/members/bulk` and `/copy` were the standing example until P14 shipped
 *     their handlers, at which point the two acquired entries. **That is the rule working, not
 *     an exception to it**: what changed is that a route now exists, so the claim became true.
 *     A reservation with no handler still gets nothing. The converse does **not** hold — eleven
 *     of the entries below have no client caller today, because the table names what the server
 *     registers and never claims that someone calls it.
 *
 * **The edge is not unified, and must not be.** `infra/*.ts` mounts greedy subtrees through
 * `routeAuthedSubtree`, so it names prefixes rather than routes; its correctness property is the
 * CORS one (`ANY` includes `OPTIONS`, and a preflight carries no `Authorization` header), which
 * per-route constants buy nothing. Importing `@selogate/core` into the SST program would be a
 * change to the deploy graph, not a tidy-up.
 */
function route(method, template, rateLimit) {
    // The one cast in the repository that produces a `RoutePath`. Everything else reads one.
    return { method, template: template, rateLimit };
}
/**
 * **Every route the platform registers**, and nothing else.
 *
 * Grouped by the PRD whose `packages/functions/*​/routes.ts` registers it. The grouping is a
 * comment rather than a field, because an entry that carried its owner would be one field away
 * from carrying its authorization.
 */
export const ROUTES = {
    /* P2 — `packages/functions/auth/routes.ts`. The first three are public by decision (§5).
       A public route has no principal to count against, so it is `unmetered` here for the same
       reason the router marks it so: the ceiling is per *caller*, and there is none yet. D65/D88's
       per-number OTP send window is the control on the first three, and it is not this one. */
    otpStart: route('POST', '/auth/otp/start', 'unmetered'),
    otpVerify: route('POST', '/auth/otp/verify', 'unmetered'),
    authRefresh: route('POST', '/auth/refresh', 'unmetered'),
    profileUpdate: route('PATCH', '/users/me', 'unmetered'),
    /** No client caller: §13's Settings → Devices screen is P15's. */
    sessionList: route('GET', '/users/me/sessions', 'unmetered'),
    sessionRevoke: route('DELETE', '/users/me/sessions/{sessionId}', 'unmetered'),
    /** No client caller, and **no PRD names a screen for it** — the one unowned absence (R3-5). */
    sessionRevokeAll: route('DELETE', '/users/me/sessions', 'unmetered'),
    /**
     * **D308's acknowledgement** — the client telling the server that the rotation it was handed
     * is durably written, so the credential held pending that news can be discarded. The wire
     * contract is on its handler in `packages/functions/auth/routes.ts`.
     *
     * **Authenticated, and deliberately not under `/auth/`.** The three sign-in routes are public
     * because they happen before a session exists; this one happens *because* one does, and a
     * device that has just refreshed holds both a fresh access token and its binding. Putting it
     * here rather than beside them is what keeps it off `REVIEWED_PUBLIC_ROUTES` and inside the
     * `/users/{proxy+}` subtree the edge already mounts with the authorizer (`AGENTS.md` §4.5) —
     * no new public surface, and no `infra/` change.
     *
     * **`current` is a literal, not a `{sessionId}`.** The binding names the session; a parameter
     * would be one the handler must then refuse to honour, which is a route that invites the
     * question *may I acknowledge for another device?* and answers it in a handler rather than in
     * the path.
     *
     * `unmetered` on the same reasoning as its neighbours, and with one of its own: it fires once
     * per rotation, roughly once per hour per signed-in device, and a ceiling that refused it
     * would only extend the life of a stored credential.
     */
    sessionRefreshAck: route('POST', '/users/me/sessions/current/refresh-ack', 'unmetered'),
    /**
     * **The client diagnostic sink** — `credential-store-recovery-handoff.md` §5/§5.1. One event
     * per credential-store write failure on the device, emitted **even when rung 1 heals it
     * silently**, which is the entire reason the route exists: a recovery that reports nothing is
     * the blindness that made a live iOS lock-out diagnosable only by a TestFlight reinstall.
     *
     * **Public by necessity, not by convenience.** The failure it reports happens while the
     * device cannot write its own credential store, so there is no session to bind and no
     * principal to name; `registerPublicRoute` is the only registration that can carry it, and
     * `router.ts`'s `REVIEWED_PUBLIC_ROUTES` is where that decision is reviewed.
     *
     * **`unmetered` here does not mean unlimited, and the distinction is this file's own (rule 3
     * above).** The posture states what a **caller** pays, and the per-caller ceiling counts a
     * `Principal`; a public route has none, so `router.ts` mounts every one of them
     * `metered: false` whatever the entry says — a `'metered'` declaration here would be one
     * nothing could honour, which is worse than no declaration because it reads as a ceiling. The
     * ceiling that *does* apply is §13's per-IP axis, declared beside the sign-in routes in
     * `PER_IP_LIMITED_PUBLIC_ROUTES` and spent through `PER_IP_RATE_LIMIT` — one limiter, re-used,
     * never a second one. This is the same answer the three sign-in routes above give, for the
     * same reason.
     *
     * **The payload resolves to no person** (D111, D109): no user id, no device id, no phone
     * number, and no stage — the stage is stamped server-side. `schemas.ts`'s declared field list
     * is that constraint written as code, and D57 **rejects** an undeclared field rather than
     * stripping it, so a later caller that adds `userId` gets a `422` naming the field instead of
     * a quiet arrival into a sink that would then be processing personal data.
     */
    clientDiagnostic: route('POST', '/diagnostics/client', 'unmetered'),
    /**
     * **R9's remote-config read — `GET /config`, unauthenticated, and the response never varies
     * by caller** (**D327**). The document it serves is one operator-owned SSM parameter
     * (`infra/data.ts`, on D106's terms); the handler is `packages/functions/auth/client-config.ts`.
     *
     * **Public by necessity in the strongest sense this file has:** the client fetches it at launch
     * **before** a session exists and on every resume whether one exists or not, and a resident
     * stranded on the OTP screen is exactly who a kill switch may need to reach. An authenticated
     * `/config` could not be read by the population a halt is aimed at.
     *
     * **It carries no caller identity in either direction** (R9 §5 item 4): no `Authorization`, no
     * `x-selogate-session`, no build header, no query string. That is what lets it be cached per
     * container for 60 s and served byte-identically to everyone — and it is why R8's cohort rules
     * ride as opaque data inside the document for the **client** to resolve, rather than being
     * evaluated here against a caller the route deliberately cannot see.
     *
     * `unmetered` on its neighbours' reasoning (rule 3 above, and `clientDiagnostic`'s note): the
     * posture states what a **caller** pays and a public route has none, so `router.ts` mounts it
     * `metered: false` whatever this says. Its address-scoped posture is the separate decision
     * **D332** records, and it is `PER_IP_EXEMPT_PUBLIC_ROUTES` — `POST /auth/refresh`'s reasoning
     * exactly: a whole installed base behind one carrier NAT calls it at every launch, and there is
     * nothing cheap to gain by abusing a public read.
     */
    clientConfig: route('GET', '/config', 'unmetered'),
    /* P3 — `packages/functions/provision/routes.ts`. */
    gateProvision: route('POST', '/gates/provision', 'unmetered'),
    /**
     * D151 — resolves a scanned `BrokerCode`'s claim status **before** the installer has typed a
     * name or address, so the "already installed" screen can render right after the scan rather
     * than after `POST /gates/provision`'s `409`. A read-only courtesy in front of that `409`
     * (`contracts.md` §1c): it never creates anything, and the transaction's own condition failure
     * stays the authoritative answer at submit time.
     */
    gateClaimLookup: route('GET', '/gates/claim/{brokerCode}', 'metered'),
    gateList: route('GET', '/gates', 'unmetered'),
    gateRead: route('GET', '/gates/{gateId}', 'metered'),
    gateUpdate: route('PATCH', '/gates/{gateId}', 'metered'),
    gateDelete: route('DELETE', '/gates/{gateId}', 'metered'),
    gateSubscription: route('GET', '/gates/{gateId}/subscription', 'metered'),
    /** No client caller: the installer's own install list is P15's. */
    installerInstalls: route('GET', '/installers/me/installs', 'unmetered'),
    /** Deployed with no caller by D138 — install-flow BLE moved to P16, the route stayed. */
    gateBleCredentials: route('PUT', '/gates/{gateId}/ble-credentials', 'metered'),
    /* P4 — `packages/functions/gate-command/routes.ts`. */
    gateOpen: route('POST', '/gates/{gateId}/open', 'metered'),
    /**
     * **R7/D277 — ONE ROUTE PER DIRECTION, and never a `command` field on `…/open`.**
     *
     * The open handler's rules are argued per command across D134, D223, D225, D232, D238 and
     * D249; a Close inherits the ones that mirror and **inverts two** — `409 gate-busy` becomes
     * Failed (the lease holder is someone *opening* the gate, so the goal is the opposite of met)
     * and `rejected` becomes Failed (R7 S1's C3/C6 measured D216's *already doing it* premise false
     * for this direction). A field on the open route would bury both inversions in a branch a later
     * refactor reads as symmetry.
     *
     * **`metered`, on `gateBleAccess`'s own argument**: it is a command-authorization surface, and
     * it pays the same ceiling from the same bucket as everything else metered — one limiter,
     * re-used, never a second one (D68/D90, D157).
     *
     * It refuses **`409 close-not-supported`** unless the gate's stored `closeMode` reads `'command'`
     * (D276/D277 (f)): the mode can change under an open Gate Detail, and a client is not the
     * safety layer.
     */
    gateClose: route('POST', '/gates/{gateId}/close', 'metered'),
    gateStatusRefresh: route('POST', '/gates/{gateId}/status/refresh', 'metered'),
    /* P16 — `packages/functions/gate-command/ble-routes.ts`, registered beside P4's pair rather
       than inside `registerGateCommandRoutes()`: they are served by their own bundle, so a
       resident-facing decrypt grant never reaches P4's open path. */
    /**
     * §9 — *"BLE-permitted grant holders: JIT `BT_NAME` + password (short-lived, audit-logged;
     * refused when the subscription is expired, D36)"*.
     *
     * **Metered, and it is the entry in this table where the posture matters most.** It is a `GET`
     * only in shape: it hands a gate credential to a client, so it is a command-authorization
     * surface, and an unmetered one would be a way to farm every gate a caller can reach at
     * whatever rate they like. It pays the same ceiling from the same bucket as everything else
     * metered — one limiter, re-used, never a second one (D68/D90, D157).
     */
    gateBleAccess: route('GET', '/gates/{gateId}/ble-access', 'metered'),
    /**
     * D25/PEC-11/PEC-12 — the reconciliation upload for a BLE open that **already happened
     * physically** while the device was offline.
     *
     * **The parent's §9 table named no endpoint for this** — the one route in this file whose path
     * an implementation PRD chose rather than transcribed. P16 logged it as an Assumption rather
     * than promoting it, because naming an endpoint the parent does not is a `D-x` at the parent.
     * **`D171` took the decision and adopted this path verbatim**, so §9's table now carries it and
     * this entry is a transcription like every other one here.
     *
     * **`receipts`, deliberately not `open`.** The two paths differ in the only way that matters —
     * one actuates a gate and one records that a gate already moved — and a receipt arriving on a
     * path with `open` in its name is one careless refactor away from being treated as a command
     * (`contracts.md` §4 rule 4, and the whole of §10 row 7).
     */
    gateReceipts: route('POST', '/gates/{gateId}/receipts', 'metered'),
    /* P5 — `packages/functions/members/routes.ts`, registered through `registerMemberRoute`. */
    memberList: route('GET', '/gates/{gateId}/members', 'metered'),
    memberInvite: route('POST', '/gates/{gateId}/members', 'metered'),
    memberRemoveSelf: route('DELETE', '/gates/{gateId}/members', 'metered'),
    memberSeen: route('POST', '/gates/{gateId}/members/seen', 'metered'),
    /**
     * P14 — the bulk pipeline and the copy. Both were **edge-only reservations** until P14
     * registered a handler: `infra/members.ts` has mounted `/gates/{gateId}/members/{proxy+}`
     * since P5, and rule 4 above is why they carried no entry while nothing served them.
     *
     * **Metered like every other gate-scoped route, and the reason is worth one line**: the
     * ceiling is per *caller*, not per row, so a manager importing forty numbers spends one
     * request against it. A per-row ceiling would be a second limiter, which is what D68/D90
     * rejected and what D134 re-parameterized rather than duplicated.
     */
    memberBulkInvite: route('POST', '/gates/{gateId}/members/bulk', 'metered'),
    memberCopy: route('POST', '/gates/{gateId}/members/copy', 'metered'),
    /**
     * **`{memberId}` is a `UserId` and nothing else since D236.** It used to be a union
     * discriminated by a **leading `+`** — an E.164 always has one, a UUID never does — so a
     * manager cancelling a pending invitation issued
     * `DELETE /gates/{id}/members/%2B972…`, and API Gateway's access log wrote `$context.path`.
     * That put **the phone number of a person with no account** into a log no erasure sweep
     * reaches, and made `security-procedure.md` §4 rule 4 untrue as written.
     *
     * **The union was the defect and not just its symptom**: one endpoint addressing two kinds of
     * subject is what made a phone number a path segment. The two below address a member; the two
     * after them address an invitation, and carry its number in a **body**.
     */
    memberUpdate: route('PATCH', '/gates/{gateId}/members/{memberId}', 'metered'),
    memberRevoke: route('DELETE', '/gates/{gateId}/members/{memberId}', 'metered'),
    /**
     * **D236 — the pending invitation's own pair.** D21's revoke-before-accept has to reach a
     * grant with no user, which is why these existed as a union in the first place; the target is
     * now `phone` in the request body.
     *
     * **`POST …/revoke` rather than `DELETE` with a body.** A body on `DELETE` is legal and
     * intermediaries are permitted to drop it — a revoke that silently becomes a no-op is worse
     * than the leak it replaces — so the destructive one takes the verb that is guaranteed to
     * carry what it needs.
     *
     * **They are NOT under `/gates/{gateId}/members/`, and that is load-bearing twice.** A literal
     * segment there would compete with `{memberId}` on the same verb, which no other entry in this
     * table does; and `/gates/{gateId}/invitations` is more specific than P3's default
     * `/gates/{proxy+}` owner, so the edge selects it rather than handing it to the provision
     * function (`infra/members.ts` mounts it explicitly for that reason).
     */
    invitationUpdate: route('PATCH', '/gates/{gateId}/invitations', 'metered'),
    invitationRevoke: route('POST', '/gates/{gateId}/invitations/revoke', 'metered'),
    gateView: route('PATCH', '/gates/{gateId}/view', 'metered'),
    /* P8 — `packages/functions/state/routes.ts`. The four Shabbat routes have no client caller;
       P15 builds the שעון שבת panel, and is the first caller of four deployed routes at once —
       exactly the situation that produced the latch `404`, with four routes instead of one. */
    gateState: route('POST', '/gates/{gateId}/state', 'metered'),
    shabbatRead: route('GET', '/gates/{gateId}/shabbat', 'metered'),
    shabbatSet: route('PUT', '/gates/{gateId}/shabbat', 'metered'),
    shabbatClear: route('DELETE', '/gates/{gateId}/shabbat', 'metered'),
    shabbatSeen: route('POST', '/gates/{gateId}/shabbat/seen', 'metered'),
    /**
     * P9 — `packages/functions/activity/routes.ts`. Manager-only filtered history (D29) **and**
     * D104's non-manager last-used stamp — a query parameter of the first route, not a second
     * reader (P09-activity-audit.md §2a rule 3).
     */
    gateActivity: route('GET', '/gates/{gateId}/activity', 'metered'),
    /**
     * Cross-gate, across every gate the caller manages — and, with `?last=true`, **D337**'s one read
     * of the caller's own last open on every gate they hold, which Home issues once per reopen in
     * place of one `gateActivity` read per gate. No Stage-1 screen reads the history half.
     *
     * **Metered for cost, not for enumeration (D157)** — the first entry in this table whose
     * posture its own path could not have expressed. One request fans out to a bounded list of
     * managed gates and queries each, so it is the most expensive read in the product; it names no
     * gate, so `isGateScopedPath()` left it unlimited and nothing in the code said as much. It
     * pays the same ceiling from the same bucket as everything else metered: one limiter,
     * re-used, never a second one.
     */
    activity: route('GET', '/activity', 'metered'),
    /**
     * P10 — `packages/functions/privacy/routes.ts`. GDPR/PPL erasure of the **caller's own**
     * account (§9): there is no subject parameter, because a route that took one would be an
     * administrative erasure, and the Monitor's own `POST /admin/users/{id}/privacy-erasure` is
     * Stage 2 and lives on a different API (M2/M6).
     *
     * **Metered**, and the reason is not enumeration: the erasure a caller can ask for is their
     * own, so there is nothing to walk. It is the single most expensive and least reversible
     * write in the product, and a retry storm against it is the one traffic shape nobody should
     * have to reason about mid-incident. It pays the same ceiling from the same bucket as every
     * other metered route.
     */
    privacyErasure: route('POST', '/privacy/erasure', 'metered'),
};
const POSTURE_BY_TEMPLATE = new Map(Object.values(ROUTES).map((entry) => [entry.template, entry.rateLimit]));
/**
 * The posture a registered path declares — what the composition root asks before it mounts a
 * route (D157).
 *
 * **An unrecognised path is `metered`, and the default is the point.** Every registration takes
 * a branded `RoutePath`, which only `ROUTES` mints, so a miss here means a deliberate
 * `as RoutePath` cast — the same forgeability the brand always had. Failing *closed* means the
 * worst case is a route that pays a ceiling its author never thought about, rather than one that
 * escapes it silently. That asymmetry is the whole reason this function exists rather than a
 * bare property read at the call site: the old `isGateScopedPath()` failed open for anything its
 * pattern did not recognise, and `GET /activity` is what that cost.
 */
export function rateLimitPostureOf(path) {
    return POSTURE_BY_TEMPLATE.get(path) ?? 'metered';
}
/**
 * The concrete path for a route — `/gates/48302917/members/6f1b…`.
 *
 * **Percent-encodes each substituted segment, and does nothing else.** It does not trim, upper-
 * case, pad, or validate an identifier: a lower-case `BrokerCode` and a `GateId`'s leading zero
 * survive it byte for byte, because repair happens once at the edge (D123) and a second
 * normalizer here would re-open the failure mode D131 records — a silently upper-cased sticker
 * code that provisions, connects, publishes, and is never heard.
 *
 * **No parameter this substitutes is personal data (D236).** The encoding is still per-segment
 * and still unconditional, because that is a property of the function rather than of what
 * happens to be passed today — but the one caller that relied on it, a phone-shaped `memberId`
 * whose leading `+` discriminated a pending invite and decodes to a space on some routers, is
 * gone: an invitation's number travels in a body now.
 *
 * Throws on a missing or empty value. The types make that unreachable from a call site that
 * compiles; the guard is for the erased-generic edges (a `RouteSpec` widened to its base type),
 * where a silently malformed `/gates//open` would be a `404` nobody could read.
 */
export function buildPath(route, ...params) {
    const values = (params[0] ?? {});
    return route.template.replace(/\{([^}]+)\}/g, (_match, name) => {
        const value = values[name];
        if (value === undefined || value === '') {
            throw new Error(`Route ${route.method} ${route.template}: missing path parameter "${name}"`);
        }
        return encodeURIComponent(value);
    });
}
//# sourceMappingURL=route-table.js.map